/**
 * DASHBOARD-RPS-TEAMS.WIDGET
 * Repartition of users for a survey
 */
import { withTranslation, WithTranslation } from "react-i18next"
import { connect } from "react-redux"
import Card from "@/components/card"
import { useEffect, useState } from "react"
import { DashboardFilters, Filter, FilterLite, FilterState } from "@/redux/filter.types"
import { store } from "@/index"
import { INSUFFICIENT_PARTICIPATION, STATUS_LOADED, STATUS_LOADING } from "@/redux/_status.types"
import { fetchRpsOverview, fetchRpsTeams } from "@/redux/_rps.actions"
import Tooltip from "@/components/tooltip"
import Link from "@/components/link"
import Plot from "react-plotly.js"
import { PLOTLY_HOVERLABEL, PLOTLY_HOVERTEMPLATE_STACKED_BARS } from "@/utils/plotly-hover.utils"
import Dropdown from "@/components/dropdown"
import Space from "@/components/space"
import { sortBy } from "lodash"
import NoResultsWidget from "./no-results.widget"
import PagePlaceholder from "@/components/page-placeholder"
import { filterReplaceDashboard } from "@/redux/filter.actions"
import { Session } from "@/redux/_session.types"
import initFilters from "@/utils/init-filters.utils"
import DashboardRpsTeamsSkeletonWidget from "./skeletons/dashboard-rps-teams-skeleton.widget"
import { motion } from "framer-motion"
import PlotReveal from "@/components/plot-reveal"
import { useRowReveal } from "@/utils/row-reveal.utils"
import { RPS_BAR_TEXT } from "@/utils/rps-bar-text.utils"

interface StateProps {
  _session: Session
  filter: FilterState
}

interface OwnProps {
  filterName?: string
  isReadOnly?: boolean
  isReport?: boolean
}

type Props = StateProps & OwnProps & WithTranslation

//Height of one team row : the left labels and the chart bands must match
const ROW_HEIGHT = 37

//Share of the people of a population in Job-Strain or Iso-Strain (red and orange of the bar), among the people whose situation could be measured
//Computed from the numbers of the bars, so that the percentage always matches what is drawn
interface StrainShare {
  isoShare: number
  jobShare: number
  total: number
  iso: number
  job: number
}

function getStrainShares(series: any): Record<string, StrainShare> {
  const names: string[] = Array.isArray(series?.attribute_name) ? series.attribute_name : []
  const shares: Record<string, StrainShare> = {}
  names.forEach((name, i) => {
    const value = (key: string) => Number(series?.[key]?.[i]) || 0
    const iso = value("iso_strain")
    const job = value("job_strain")
    const total = iso + job + value("active") + value("passive") + value("relaxed")
    if (total > 0) shares[name] = { isoShare: Math.round((iso / total) * 100), jobShare: Math.round((job / total) * 100), total, iso, job }
  })
  return shares
}

//Same share over all the people of the survey, from the counts of the overview
function getOverallShare(overview: any): StrainShare | null {
  if (!overview || overview.error || overview.message === "no_data") return null
  const iso = Number(overview.countStressed) || 0
  const job = Number(overview.countJobStrain) || 0
  const total = iso + job + (Number(overview.countActive) || 0) + (Number(overview.countPassive) || 0) + (Number(overview.countRelaxed) || 0)
  if (total <= 0) return null
  return { isoShare: Math.round((iso / total) * 100), jobShare: Math.round((job / total) * 100), total, iso, job }
}

//Share written in a chip : a share that rounds to 0 while somebody is concerned stays visible
const formatShare = (share: number, count: number): string => (share === 0 && count > 0 ? "< 1 %" : `${share} %`)

//Chip in two halves, like the start of the bar : red for Iso-Strain, then orange for Job-Strain, with the detail on hover
//A half at 0 % is not drawn (it would only raise questions) : the other one is alone, on its side, and a population without anybody in these two situations has no chip
function StrainChip({ strain, t }: { strain: StrainShare; t: any }) {
  if (strain.iso === 0 && strain.job === 0) return null

  const half = (backgroundColor: string, label: string) => (
    <span style={{ backgroundColor, color: "#ffffff", boxSizing: "border-box", minWidth: 52, padding: "1px 6px", textAlign: "center" }}>{label}</span>
  )

  return (
    <Tooltip
      text={t("rps_teams_strain_detail", {
        total: strain.total,
        iso: strain.iso,
        isoShare: strain.isoShare,
        job: strain.job,
        jobShare: strain.jobShare
      })}
      position="top"
      usePortal>
      <span className="flex text-sm font-bold" style={{ borderRadius: 999, overflow: "hidden" }}>
        {strain.iso > 0 && half("#eb5a46", formatShare(strain.isoShare, strain.iso))}
        {strain.job > 0 && half("#ff9500", formatShare(strain.jobShare, strain.job))}
      </span>
    </Tooltip>
  )
}

function DashboardRpsTeamsWidget(props: Props) {
  const rowReveal = useRowReveal()
  const { t } = props

  //State
  // Init filterName (first element of the list)
  // status : loader
  // teams : list of attributes on the left of the graph
  const [currentFilter, setCurrentFilter] = useState<Filter>(initCurrentFilter())
  const [status, setStatus] = useState(STATUS_LOADING)
  const [teams, setTeams] = useState<any>([])
  const [strainShares, setStrainShares] = useState<Record<string, StrainShare>>({})
  const [overall, setOverall] = useState<StrainShare | null>(null) //Share over all the people of the survey (with the filters of the dashboard), to compare each population with
  const [noData, setNoData] = useState(false)
  const [data, setData] = useState<Plotly.Data[]>([])

  //Name of the account when the dashboard is not filtered, otherwise the figure is not the one of the whole account
  const dashboardFilter = props.filter.dashboard
  const isFiltered = Boolean(
    dashboardFilter.gender ||
      dashboardFilter.birthDate ||
      dashboardFilter.companyWelcomeDate ||
      dashboardFilter.customFilters.length ||
      dashboardFilter.accounts.length
  )
  const overallLabel = !isFiltered && props._session.accountName ? props._session.accountName : t("rps_teams_overall")

  //Situations shown by the chips (the populations and the line of the whole account)
  const shownStrains: StrainShare[] = [...Object.values(strainShares), ...(overall ? [overall] : [])]
  const hasIsoStrain = shownStrains.some((strain) => strain.iso > 0)
  const hasJobStrain = shownStrains.some((strain) => strain.job > 0)

  const chartHeight = Math.max(teams.length, 1) * ROW_HEIGHT
  const layout: any = {
    hoverlabel: PLOTLY_HOVERLABEL,
    margin: {
      l: 0,
      r: 0,
      b: 0,
      t: 0
    },
    showlegend: false,
    bargap: 0.1,
    barcornerradius: 10,
    barmode: "stack",
    xaxis: {
      showgrid: false,
      showline: false,
      zeroline: false,
    },
    yaxis: {
      showgrid: false,
      showline: false,
      zeroline: false,
    },
    height: chartHeight
  }

  useEffect(() => {
    setCurrentFilter(initCurrentFilter())
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.filterName])

  //Load data on every change on filters and filterName
  useEffect(() => {
    async function load() {
      //Load data from server
      if (currentFilter) {
        setStatus(STATUS_LOADING)
        const data: any = await store.dispatch(
          fetchRpsTeams(currentFilter.name, props.filter.dashboard, undefined, undefined, props.isReport)
        )
        setStatus(STATUS_LOADED)

        const overview: any = await store.dispatch(fetchRpsOverview(props.filter.dashboard, undefined, undefined, props.isReport))
        setOverall(getOverallShare(overview))

        const isEmpty =
          data.message === "no_data" ||
          (Array.isArray(data.teamsRecords) && data.teamsRecords.length === 0) ||
          (Array.isArray(data.teamsSeries?.attribute_name) && data.teamsSeries.attribute_name.length === 0)

        if (isEmpty) {
          setNoData(true)
        } else if (!data.error) {
          const rpsColors: any = {
            isoStrain: "#eb5a46",
            jobStrain: "#ff9500",
            active: "#8c8c8c",
            passive: "#afafaf",
            relaxed: "#ccc"
          }

          const traceIsoStrain: Plotly.Data = {
            x: data.teamsSeries.iso_strain,
            y: data.teamsSeries.attribute_name,
            name: t("rps_stressed"),
            hovertemplate: PLOTLY_HOVERTEMPLATE_STACKED_BARS,
            text: data.teamsSeries.iso_strain,
            ...RPS_BAR_TEXT,
            orientation: "h",
            marker: {
              color: rpsColors.isoStrain,
              width: 1
            },
            type: "bar"
          }

          const traceJobStrain: Plotly.Data = {
            x: data.teamsSeries.job_strain,
            y: data.teamsSeries.attribute_name,
            name: t("rps_job_strain"),
            hovertemplate: PLOTLY_HOVERTEMPLATE_STACKED_BARS,
            text: data.teamsSeries.job_strain,
            ...RPS_BAR_TEXT,
            orientation: "h",
            marker: {
              color: rpsColors.jobStrain,
              width: 1
            },
            type: "bar"
          }

          const traceActive: Plotly.Data = {
            x: data.teamsSeries.active,
            y: data.teamsSeries.attribute_name,
            name: t("rps_active"),
            hovertemplate: PLOTLY_HOVERTEMPLATE_STACKED_BARS,
            text: data.teamsSeries.active,
            ...RPS_BAR_TEXT,
            orientation: "h",
            marker: {
              color: rpsColors.active,
              width: 1
            },
            type: "bar"
          }

          const tracePassive: Plotly.Data = {
            x: data.teamsSeries.passive,
            y: data.teamsSeries.attribute_name,
            name: t("rps_passive"),
            hovertemplate: PLOTLY_HOVERTEMPLATE_STACKED_BARS,
            text: data.teamsSeries.passive,
            ...RPS_BAR_TEXT,
            orientation: "h",
            marker: {
              color: rpsColors.passive,
              width: 1
            },
            type: "bar"
          }

          const traceRelaxed: Plotly.Data = {
            x: data.teamsSeries.relaxed,
            y: data.teamsSeries.attribute_name,
            name: t("rps_relaxed"),
            hovertemplate: PLOTLY_HOVERTEMPLATE_STACKED_BARS,
            text: data.teamsSeries.relaxed,
            ...RPS_BAR_TEXT,
            orientation: "h",
            marker: {
              color: rpsColors.relaxed,
              width: 1
            },
            type: "bar"
          }

          setData([traceIsoStrain, traceJobStrain, traceActive, tracePassive, traceRelaxed])
          setStrainShares(getStrainShares(data.teamsSeries))
          //Sort teams
          setTeams(sortBy(data.teamsRecords, ["ratio", "iso_strain", "job_strain", "attribute_id"]).reverse())
        }
      } else {
        setStatus(STATUS_LOADED)
      }
    }

    load()
  }, [t, currentFilter, props.filter.dashboard])

  function clickTeams(team) {
    store.dispatch(
      filterReplaceDashboard(
        new DashboardFilters({
          customFilters: [new FilterLite({ id: currentFilter.id, attributesIds: team.attribute_id.split("_") })]
        })
      )
    )
  }

  //Return default heatmap filter
  function initCurrentFilter() {
    let searchFilter: Filter | undefined = undefined
    if (props.filterName) {
      searchFilter = props.filter.list.find((x: Filter) => x.name === props.filterName)
    } else if (props._session.accountOptions.heatmapFilterName) {
      searchFilter = props.filter.list.find((x: Filter) => x.name === props._session.accountOptions.heatmapFilterName)
    }

    if (searchFilter) {
      return searchFilter
    } else {
      return props.filter.list.length ? props.filter.list[0] : new Filter()
    }
  }

  return status === INSUFFICIENT_PARTICIPATION ? (
    <NoResultsWidget />
  ) : (
    <Card className="flex1" status={status} helpSection="onglets" skeleton={<DashboardRpsTeamsSkeletonWidget />}>
      {noData ? (
        <PagePlaceholder
          image="sherlock"
          title={t("rps_teams_no_data_title")}
          help={t("survey_no_results_help", { count: props._session.participationMin })}
        />
      ) : (
        <>
          {!props.isReport && (
            <div className="flex" style={{ paddingTop: 12 }}>
              <div>
                <h2>{t("rps_teams_title")}</h2>
              </div>
              <Space />
              {props.isReadOnly ? (
                <p>{currentFilter.name}</p>
              ) : (
                <Dropdown
                  displayField="name"
                  list={initFilters(true, true)}
                  onSelect={(filter: Filter) => setCurrentFilter(filter)}
                  value={currentFilter.name}
                />
              )}
            </div>
          )}
        </>
      )}

      {overall && teams.length > 0 && (
        <div
          className="flex"
          style={{ alignItems: "center", borderBottom: "1px solid #e3e3e8", height: ROW_HEIGHT, marginTop: 12, width: "100%" }}>
          <div className="font-bold" style={{ flex: "0 0 200px", minWidth: 0 }}>
            {overallLabel}
          </div>
          <div style={{ flex: "0 0 124px", minWidth: 0 }}>
            <div style={{ alignItems: "center", display: "flex", height: ROW_HEIGHT, justifyContent: "flex-end", paddingRight: 14 }}>
              <StrainChip strain={overall} t={t} />
            </div>
          </div>
        </div>
      )}

      <div
        className="flex"
        style={{ marginTop: overall ? "6px" : "12px", width: "100%", minWidth: 0, overflow: "hidden", alignItems: "flex-start" }}>
        <div style={{ flex: "0 0 200px", minWidth: 0 }}>
          {teams.map((team: any, i: number) => (
            <motion.div
              key={team.attribute_id}
              {...rowReveal(i)}
              title={team.attribute_name}
              style={{
                alignItems: "center",
                display: "flex",
                height: ROW_HEIGHT,
                marginRight: 20,
                minWidth: 0
              }}>
              <Link className="rps-team-name" isWithoutMargin onClick={() => clickTeams(team)}>
                {team.attribute_name}
              </Link>
            </motion.div>
          ))}
        </div>

        <div style={{ flex: "0 0 124px", minWidth: 0 }}>
          {teams.map((team: any, i: number) => {
            const strain = strainShares[team.attribute_name]
            return (
              <motion.div
                key={team.attribute_id}
                {...rowReveal(i)}
                style={{ alignItems: "center", display: "flex", height: ROW_HEIGHT, justifyContent: "flex-end", paddingRight: 14 }}>
                {strain && (
                  <StrainChip strain={strain} t={t} />
                )}
              </motion.div>
            )
          })}
        </div>

        <div style={{ flex: "1 1 auto", minWidth: 0, overflow: "hidden" }}>
          <PlotReveal>
            <Plot
              className="rps-teams-chart"
              data={data}
              layout={layout}
              config={{
                displayModeBar: false
              }}
              useResizeHandler
              style={{ width: "100%", height: chartHeight }}
            />
          </PlotReveal>
        </div>
      </div>

      {/* The legend only names what the chips show : no orange entry when nobody is in Job-Strain, no legend when there is no chip */}
      {teams.length > 0 && (hasIsoStrain || hasJobStrain) && (
        <div className="grey-t text-sm flex" style={{ marginTop: 12, marginLeft: 200, gap: 20 }}>
          {hasIsoStrain && (
            <span className="inline-flex items-center" style={{ gap: 6 }}>
              <span style={{ backgroundColor: "#eb5a46", borderRadius: 999, height: 8, width: 8 }} />
              {t("rps_teams_strain_legend_iso")}
            </span>
          )}
          {hasJobStrain && (
            <span className="inline-flex items-center" style={{ gap: 6 }}>
              <span style={{ backgroundColor: "#ff9500", borderRadius: 999, height: 8, width: 8 }} />
              {t("rps_teams_strain_legend_job")}
            </span>
          )}
        </div>
      )}
    </Card>
  )
}

const mapStateToProps = (state) => ({
  _session: state._session,
  filter: state.filter
})

export default connect(mapStateToProps)(withTranslation()(DashboardRpsTeamsWidget))
